import { useState, useEffect } from 'react';
import api, { getTenants, createTenant, updateTenant, deleteTenant, getUnits, getProperties, adminResetPassword } from '../services/api';
import { Users, Plus, Trash, PencilSimple, X, EnvelopeSimple, Phone, CalendarBlank, Key, Copy, Check, PaperPlaneTilt, LockKey } from '@phosphor-icons/react';

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const resetTenantPassword = async (tenantId, tenantName) => {
    const pw = window.prompt(`Neues Portal-Passwort für ${tenantName} eingeben (mind. 8 Zeichen):`);
    if (!pw) return;
    if (pw.length < 8) { alert('Passwort muss mindestens 8 Zeichen haben'); return; }
    try {
      const res = await adminResetPassword({ tenant_id: tenantId, new_password: pw });
      alert(`✓ Passwort für ${res.data.tenant_name || tenantName} wurde zurückgesetzt.\n\nNeue Login-Daten:\nE-Mail: ${res.data.tenant_email}\nPasswort: ${pw}\n\nBitte sicher an den Mieter weitergeben.`);
    } catch (e) {
      alert('Fehler: ' + (e.response?.data?.detail || e.message));
    }
  };

  const resendInvitation = async (tenantId, tenantName) => {
    if (!window.confirm(`Einladungs-E-Mail an ${tenantName} senden? Der bisherige Code wird neu generiert.`)) return;
    try {
      const res = await api.post(`/tenants/${tenantId}/resend-invitation`);
      if (res.data?.sent) {
        alert(`E-Mail wurde an ${res.data.email} versendet.\nNeuer Code: ${res.data.invite_code}`);
        loadTenants();
      } else {
        alert('Versand fehlgeschlagen (SMTP nicht erreichbar?).');
      }
    } catch (e) {
      alert('Fehler: ' + (e.response?.data?.detail || e.message));
    }
  };

  const loadData = async () => {
    try {
      const [tenantsRes, unitsRes, propertiesRes] = await Promise.all([
        getTenants(),
        getUnits(),
        getProperties()
      ]);
      setTenants(tenantsRes.data);
      setUnits(unitsRes.data);
      setProperties(propertiesRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diesen Mieter wirklich löschen?')) return;
    try {
      await deleteTenant(id);
      loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const getUnitInfo = (unitId) => {
    if (!unitId) return null;
    const unit = units.find(u => u.id === unitId);
    if (!unit) return null;
    const property = properties.find(p => p.id === unit.property_id);
    return { unit, property };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenants-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-black tracking-tight text-slate-900">Mieter</h1>
          <p className="text-slate-500 mt-1">Verwalten Sie Ihre Mieterverhältnisse</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-slate-900 text-white px-6 py-2.5 font-medium hover:bg-slate-800 flex items-center gap-2"
          data-testid="add-tenant-button"
        >
          <Plus weight="bold" className="w-4 h-4" />
          Mieter hinzufügen
        </button>
      </div>

      {/* Tenants List */}
      {tenants.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <Users weight="light" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Keine Mieter</h3>
          <p className="text-slate-500 mb-6">Fügen Sie Ihren ersten Mieter hinzu.</p>
          <button
            onClick={() => setShowModal(true)}
            className="bg-slate-900 text-white px-6 py-2.5 font-medium hover:bg-slate-800 inline-flex items-center gap-2"
          >
            <Plus weight="bold" className="w-4 h-4" />
            Ersten Mieter hinzufügen
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]" data-testid="tenants-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Name</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Kontakt</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Einheit</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Personen</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Portal</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tenants.map((tenant) => {
                const unitInfo = getUnitInfo(tenant.unit_id);
                return (
                  <tr key={tenant.id} className="hover:bg-gray-50 transition-colors" data-testid={`tenant-row-${tenant.id}`}>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 flex items-center justify-center text-slate-600 font-bold">
                          {tenant.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-slate-900">{tenant.name}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm space-y-1">
                        {tenant.email && (
                          <div className="flex items-center gap-2 text-slate-500">
                            <EnvelopeSimple weight="regular" className="w-4 h-4" />
                            {tenant.email}
                          </div>
                        )}
                        {tenant.phone && (
                          <div className="flex items-center gap-2 text-slate-500">
                            <Phone weight="regular" className="w-4 h-4" />
                            {tenant.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {unitInfo ? (
                        <div className="text-sm">
                          <p className="font-medium text-slate-900">{unitInfo.unit.name}</p>
                          <p className="text-slate-500">{unitInfo.property?.name}</p>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-1.5 text-sm text-slate-700">
                        <Users weight="bold" className="w-4 h-4 text-slate-400" />
                        {tenant.personen_anzahl || 1}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {tenant.has_portal_access ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-100 text-emerald-800 rounded font-medium">
                          <Check weight="bold" className="w-3 h-3" /> Aktiv
                        </span>
                      ) : tenant.invite_code ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => copyInviteCode(tenant.invite_code)}
                            className="inline-flex items-center gap-1.5 px-2 py-1 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded hover:bg-amber-100 font-mono"
                            data-testid={`copy-invite-${tenant.id}`}
                            title="Einladungscode kopieren"
                          >
                            <Key weight="bold" className="w-3 h-3" />
                            {copiedCode === tenant.invite_code ? 'Kopiert!' : tenant.invite_code}
                          </button>
                          {tenant.email && (
                            <button
                              onClick={() => resendInvitation(tenant.id, tenant.name)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              title="Einladung per E-Mail versenden"
                              data-testid={`resend-invite-${tenant.id}`}
                            >
                              <PaperPlaneTilt weight="bold" className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-right">
                      {tenant.email && (
                        <button
                          onClick={() => resetTenantPassword(tenant.id, tenant.name)}
                          className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          title="Mieter-Portal-Passwort zurücksetzen"
                          data-testid={`reset-pw-tenant-${tenant.id}`}
                        >
                          <LockKey weight="bold" className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => { setEditingTenant(tenant); setShowModal(true); }}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                        data-testid={`edit-tenant-${tenant.id}`}
                      >
                        <PencilSimple weight="bold" className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(tenant.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        data-testid={`delete-tenant-${tenant.id}`}
                      >
                        <Trash weight="bold" className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <TenantModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditingTenant(null); }}
        tenant={editingTenant}
        units={units}
        properties={properties}
        onSave={() => { loadData(); setShowModal(false); setEditingTenant(null); }}
      />
    </div>
  );
}

function TenantModal({ open, onClose, tenant, units, properties, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    unit_id: '',
    lease_start: '',
    lease_end: '',
    personen_anzahl: 1,
    miete_kalt: '',
    miete_nk_vorauszahlung: '',
    miete_heizung_vorauszahlung: ''
  });
  const [propertyFilter, setPropertyFilter] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        email: tenant.email || '',
        phone: tenant.phone || '',
        unit_id: tenant.unit_id || '',
        lease_start: tenant.lease_start || '',
        lease_end: tenant.lease_end || '',
        personen_anzahl: tenant.personen_anzahl || 1,
        miete_kalt: tenant.miete_kalt ?? '',
        miete_nk_vorauszahlung: tenant.miete_nk_vorauszahlung ?? '',
        miete_heizung_vorauszahlung: tenant.miete_heizung_vorauszahlung ?? ''
      });
      // Vor-Auswahl Objekt-Filter anhand der Einheit des Mieters
      const currentUnit = units.find(u => u.id === tenant.unit_id);
      setPropertyFilter(currentUnit?.property_id || '');
    } else {
      setFormData({ name: '', email: '', phone: '', unit_id: '', lease_start: '', lease_end: '', personen_anzahl: 1, miete_kalt: '', miete_nk_vorauszahlung: '', miete_heizung_vorauszahlung: '' });
      setPropertyFilter('');
    }
  }, [tenant, units]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const num = (v) => (v === '' || v === null || v === undefined) ? null : parseFloat(String(v).replace(',', '.'));
      const data = {
        ...formData,
        unit_id: formData.unit_id || null,
        personen_anzahl: parseInt(formData.personen_anzahl) || 1,
        miete_kalt: num(formData.miete_kalt),
        miete_nk_vorauszahlung: num(formData.miete_nk_vorauszahlung),
        miete_heizung_vorauszahlung: num(formData.miete_heizung_vorauszahlung)
      };
      if (tenant) {
        await updateTenant(tenant.id, data);
      } else {
        await createTenant(data);
      }
      onSave();
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setLoading(false);
    }
  };

  const getUnitOptions = () => {
    const filtered = propertyFilter
      ? units.filter(u => u.property_id === propertyFilter)
      : units;
    return filtered.map(unit => {
      const property = properties.find(p => p.id === unit.property_id);
      return {
        id: unit.id,
        label: `${unit.name} (${property?.name || 'Unbekannt'})`
      };
    });
  };

  // Wenn das ausgewählte Objekt sich ändert und die aktuell gewählte Einheit
  // nicht mehr zum Objekt passt, Einheit zurücksetzen.
  useEffect(() => {
    if (!propertyFilter || !formData.unit_id) return;
    const currentUnit = units.find(u => u.id === formData.unit_id);
    if (currentUnit && currentUnit.property_id !== propertyFilter) {
      setFormData(prev => ({ ...prev, unit_id: '' }));
    }
  }, [propertyFilter, units, formData.unit_id]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="tenant-modal">
      <div className="bg-white w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="font-heading text-xl font-bold text-slate-900">
            {tenant ? 'Mieter bearbeiten' : 'Neuer Mieter'}
          </h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
            <X weight="bold" className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
              placeholder="Max Mustermann"
              required
              data-testid="tenant-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">E-Mail</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
                placeholder="max@email.de"
                data-testid="tenant-email-input"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Telefon</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
                placeholder="+49 123 456789"
                data-testid="tenant-phone-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Objekt</label>
              <select
                value={propertyFilter}
                onChange={(e) => setPropertyFilter(e.target.value)}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
                data-testid="tenant-property-filter-select"
              >
                <option value="">Alle Objekte</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Einheit</label>
              <select
                value={formData.unit_id}
                onChange={(e) => setFormData(prev => ({ ...prev, unit_id: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
                data-testid="tenant-unit-select"
              >
                <option value="">Keine Einheit zugewiesen</option>
                {getUnitOptions().map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Mietbeginn</label>
              <input
                type="date"
                value={formData.lease_start}
                onChange={(e) => setFormData(prev => ({ ...prev, lease_start: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
                data-testid="tenant-lease-start-input"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Mietende</label>
              <input
                type="date"
                value={formData.lease_end}
                onChange={(e) => setFormData(prev => ({ ...prev, lease_end: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
                data-testid="tenant-lease-end-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
              Anzahl Personen im Haushalt
            </label>
            <input
              type="number"
              min="1"
              value={formData.personen_anzahl}
              onChange={(e) => setFormData(prev => ({ ...prev, personen_anzahl: e.target.value }))}
              className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
              data-testid="tenant-personen-input"
              required
            />
            <p className="text-xs text-slate-500 mt-1">Wichtig für Verteilerschlüssel (z.B. Müll, Wasser)</p>
          </div>

          {/* Mietkosten */}
          <div className="border-t border-slate-100 pt-5">
            <p className="text-xs tracking-widest uppercase font-bold text-slate-500 mb-3">Mietkosten (€ / Monat)</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-slate-600 mb-1">Kaltmiete</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.miete_kalt}
                  onChange={(e) => setFormData(prev => ({ ...prev, miete_kalt: e.target.value }))}
                  placeholder="800,00"
                  className="w-full border-b border-gray-300 bg-transparent py-2 font-mono focus:border-slate-900 outline-none"
                  data-testid="tenant-miete-kalt-input"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">NK-Vorauszahlung</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.miete_nk_vorauszahlung}
                  onChange={(e) => setFormData(prev => ({ ...prev, miete_nk_vorauszahlung: e.target.value }))}
                  placeholder="150,00"
                  className="w-full border-b border-gray-300 bg-transparent py-2 font-mono focus:border-slate-900 outline-none"
                  data-testid="tenant-miete-nk-input"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Heizkosten-VZ</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.miete_heizung_vorauszahlung}
                  onChange={(e) => setFormData(prev => ({ ...prev, miete_heizung_vorauszahlung: e.target.value }))}
                  placeholder="80,00"
                  className="w-full border-b border-gray-300 bg-transparent py-2 font-mono focus:border-slate-900 outline-none"
                  data-testid="tenant-miete-heizung-input"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Beim Speichern werden für jeden Monat ab Mietbeginn automatisch Soll-Einträge im Mietkonto erstellt (siehe „Mieteinnahmen").
            </p>
          </div>
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-200 text-slate-900 py-2.5 font-medium hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-slate-900 text-white py-2.5 font-medium hover:bg-slate-800 disabled:opacity-50"
              data-testid="tenant-save-button"
            >
              {loading ? 'Wird gespeichert...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
